import React, { Dispatch, FormEvent, SetStateAction, useState } from "react";
import { signIn } from "aws-amplify/auth";
import { clearError, showError } from "../helpers/formHelper";

interface SignInFormProps {
  setNeedsNewPassword: Dispatch<SetStateAction<boolean>>;
}

interface SignInFormElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
}

interface SignInFormElement extends HTMLFormElement {
  readonly elements: SignInFormElements;
}

export function SignInForm({ setNeedsNewPassword }: SignInFormProps) {
  const [error, setError] = useState("");

  async function onSubmit(event: FormEvent<SignInFormElement>) {
    event.preventDefault();

    const form = event.currentTarget;
    const { email, password } = form.elements;

    clearError(setError, form);

    if (!email.validity.valid && !password.validity.valid) {
      return showError("Please enter a valid email and password.", setError, form);
    }
    if (!email.validity.valid) {
      return showError("Please enter a valid email.", setError, form, email);
    }
    if (!password.validity.valid) {
      return showError("Please enter a password.", setError, form, password);
    }

    try {
      const { nextStep } = await signIn({
        username: email.value,
        password: password.value,
      });

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setNeedsNewPassword(true);
      }
      else if (nextStep.signInStep !== "DONE") {
        showError(
          `Sign in failed. Please try again. If the problem persists, contact an administrator,
          quoting: nextStep.signInStep=${nextStep.signInStep}`,
          setError,
          form);
      }
    }
    catch (err) {
      if (err instanceof Error && err.name === "NotAuthorizedException") {
        showError("Sign in details incorrect. Please try again.", setError, form);
      }
      else {
        showError("Something went wrong. Please try again.", setError, form);
      }
    }
  }

  return (
    <form noValidate onSubmit={onSubmit} className="d-flex flex-column gap-2 w-100 mt-4">
      <label htmlFor="email">Email address</label>
      <input className="form-control" type="email" id="email" name="email" required />

      <label htmlFor="password">Password</label>
      <input
        className="form-control"
        type="password"
        id="password"
        name="password"
        required
      />

      <button className="btn btn-success">
        <i className="bi bi-box-arrow-in-right"></i> Sign in
      </button>
      <div className="invalid-feedback">{error}</div>
    </form>
  );
}
